/**
 * ``useAnnotationDraw`` — Terra Draw on the map for **drawing** annotations
 * (ADR-034; feature-spec §5.2 / §8). As of the design-update PR D, persisted
 * annotations are **displayed** by the Meridian MapLibre layers
 * (:file:`@/map/annotation-render-layer.tsx`) — Terra Draw no longer renders
 * them — so we control the render (incl. the per-shape dash). This hook owns:
 *
 *   - the **draw modes** (point / linestring / circle / polygon) the palette
 *     tool selects via :meth:`setMode`; an idle ``render`` mode (no features —
 *     just the resting mode when no tool is active); and a registered
 *     ``select`` mode (Terra Draw's ``removeFeatures`` needs one internally).
 *   - **draw → persist:** on ``finish`` the completed shape becomes an
 *     :type:`AnnotationWriteRequest` (:func:`finishedFeatureToRequest`), the
 *     transient draw feature is dropped, it's POSTed (:func:`useCreateAnnotation`),
 *     and the tool deselects (D14 amendment). The Meridian render layer paints
 *     the persisted copy once the list refetches.
 *   - **selection:** with no tool active, a map click selects the annotation
 *     under the cursor (``queryRenderedFeatures`` → ``annotation_id`` off a
 *     Meridian render-layer feature → the tool store's ``selectedId``).
 *   - the **crosshair cursor** while a draw tool is active.
 *   - a **live measurement readout** while drawing — a map-anchored chip showing
 *     the in-progress shape's running measurement (line distance + azimuth, circle
 *     radius, polygon area + perimeter), updated on every Terra Draw ``change`` so
 *     the operator can measure between points / size a shape as they draw
 *     (annotation-draw-measure).
 *
 * Lifecycle: constructed once the map is usable (``isMapUsable``); ``stop()`` on
 * teardown. WebGL-only — verified in the deferred §5.11 visual pass; the pure
 * pieces it composes (annotation-draw-request, annotation-map-features) are
 * unit-tested.
 *
 * Cross-references:
 *   - ADR-034 design update; feature-spec §5.2 / §8 / D14.
 *   - @/map/annotation-render-layer (the display path).
 *   - @/annotations/{use-annotations, annotation-draw-request, annotation-tool-store}.
 */
import { useEffect, useRef } from "react";
import {
  TerraDraw,
  TerraDrawCircleMode,
  TerraDrawLineStringMode,
  TerraDrawPointMode,
  TerraDrawPolygonMode,
  TerraDrawRenderMode,
  TerraDrawSelectMode,
  type HexColor,
} from "terra-draw";
import { TerraDrawMapLibreGLAdapter } from "terra-draw-maplibre-gl-adapter";
import { Popup, type Map as MapLibreMap, type MapMouseEvent } from "maplibre-gl";
import { isMapUsable } from "@/map/map-ready";
import { useCreateAnnotation } from "@/annotations/use-annotations";
import { ANNOTATION_RENDER_MODE } from "@/annotations/annotation-circle-geometry";
import { finishedFeatureToRequest } from "@/annotations/annotation-draw-request";
import { draftMeasurement } from "@/annotations/annotation-draw-measure";
import { resolveAnnotationClick } from "@/annotations/annotation-select-hit";
import { isBackgroundClick } from "@/map/background-deselect";
import { usePreferences } from "@/preferences/preferences-context";
import { useAnnotationToolStore, type AnnotationTool } from "@/annotations/annotation-tool-store";

const DEFAULT_STROKE: HexColor = "#f5a623";
const DEFAULT_POINT_OUTLINE: HexColor = "#0f1117";

/**
 * Map the active palette tool → the Terra Draw mode name. No active tool falls
 * back to the static ``render`` mode (idle — with no tool a click selects an
 * existing annotation rather than drawing).
 */
function toolToDrawMode(tool: AnnotationTool | null): string {
  switch (tool) {
    case "marker":
      return "point";
    case "line":
      return "linestring";
    case "circle":
      return "circle";
    case "polygon":
      return "polygon";
    default:
      return ANNOTATION_RENDER_MODE;
  }
}

/**
 * Mount Terra Draw annotation drawing on the map. Call once with the loaded map
 * (``null`` before load / after teardown — a no-op until then). Renders nothing
 * itself; the persisted annotations are displayed by AnnotationRenderLayer.
 */
export function useAnnotationDraw(map: MapLibreMap | null): void {
  const drawRef = useRef<TerraDraw | null>(null);
  const createAnnotation = useCreateAnnotation();
  const activeTool = useAnnotationToolStore((state) => state.activeTool);

  // Latest-ref for the create mutation so the once-registered ``finish`` handler
  // always calls the current ``mutate`` (the mutation object is recreated per render).
  const createMutateRef = useRef(createAnnotation.mutate);
  useEffect(() => {
    createMutateRef.current = createAnnotation.mutate;
  });

  // The operator's live distance unit for the in-progress draw readout — kept in a
  // ref so the once-registered ``change`` handler always formats with the current unit.
  const distanceUnit = usePreferences().workingState.units?.distance ?? "nautical_miles";
  const unitRef = useRef(distanceUnit);
  useEffect(() => {
    unitRef.current = distanceUnit;
  });
  // Lets the tool-change effect hide a stale draw readout (the popup lives in the
  // construct effect's closure).
  const hideDraftMeasureRef = useRef<() => void>(() => undefined);

  // Construct + start Terra Draw once the map is usable; tear down on unmount.
  useEffect(() => {
    if (map === null || !isMapUsable(map)) {
      return undefined;
    }
    const draw = new TerraDraw({
      adapter: new TerraDrawMapLibreGLAdapter({ map }),
      modes: [
        // The idle mode (no tool active). Persisted annotations are NOT added to
        // it — they're displayed by the Meridian MapLibre layers
        // (AnnotationRenderLayer); the static styles are never applied (no features).
        new TerraDrawRenderMode({
          modeName: ANNOTATION_RENDER_MODE,
          styles: { lineStringColor: DEFAULT_STROKE, polygonOutlineColor: DEFAULT_STROKE },
        }),
        new TerraDrawPointMode({
          styles: {
            pointColor: DEFAULT_STROKE,
            pointWidth: 6,
            pointOutlineColor: DEFAULT_POINT_OUTLINE,
            pointOutlineWidth: 1,
          },
        }),
        new TerraDrawLineStringMode({
          styles: {
            lineStringColor: DEFAULT_STROKE,
            lineStringWidth: 2,
            closingPointColor: DEFAULT_STROKE,
            closingPointWidth: 4,
            closingPointOutlineColor: DEFAULT_POINT_OUTLINE,
            closingPointOutlineWidth: 1,
          },
        }),
        new TerraDrawCircleMode({
          styles: {
            fillColor: DEFAULT_STROKE,
            fillOpacity: 0.1,
            outlineColor: DEFAULT_STROKE,
            outlineWidth: 2,
          },
        }),
        new TerraDrawPolygonMode({
          styles: {
            fillColor: DEFAULT_STROKE,
            fillOpacity: 0.1,
            outlineColor: DEFAULT_STROKE,
            outlineWidth: 2,
            closingPointColor: DEFAULT_STROKE,
            closingPointWidth: 4,
            closingPointOutlineColor: DEFAULT_POINT_OUTLINE,
            closingPointOutlineWidth: 1,
          },
        }),
        // A select mode MUST be registered for Terra Draw's removeFeatures() to
        // work — its internal deselect calls getSelectModeOrThrow(). We never
        // setMode("select") (selection is the map-click handler below);
        // registering it just satisfies that internal path (ADR-034 bug fix —
        // "No select mode defined in instance" on delete).
        new TerraDrawSelectMode(),
      ],
    });
    draw.start();
    draw.setMode(toolToDrawMode(useAnnotationToolStore.getState().activeTool));

    // --- Live measurement readout while drawing (ADR-034) --------------------
    // A small map-anchored chip showing the running measurement of the IN-PROGRESS
    // shape — distance + azimuth for a line, radius for a circle, area + perimeter
    // for a polygon — so the operator can measure between two points or size a
    // shape as they draw. Reuses the readout-tooltip styling; hidden on finish /
    // tool change / teardown.
    const measurePopup = new Popup({
      closeButton: false,
      closeOnClick: false,
      className: "annotation-measurement-tooltip annotation-draw-measurement",
      offset: 14,
    });
    let measureShown = false;
    const hideDraftMeasure = (): void => {
      if (measureShown) {
        measurePopup.remove();
        measureShown = false;
      }
    };
    hideDraftMeasureRef.current = hideDraftMeasure;
    const refreshDraftMeasure = (): void => {
      if (useAnnotationToolStore.getState().activeTool === null) {
        hideDraftMeasure();
        return;
      }
      let measurement: ReturnType<typeof draftMeasurement> = null;
      for (const draft of draw.getSnapshot()) {
        measurement = draftMeasurement(draft, unitRef.current);
        if (measurement !== null) {
          break;
        }
      }
      if (measurement === null) {
        hideDraftMeasure();
        return;
      }
      measurePopup.setLngLat(measurement.anchor).setText(measurement.text).addTo(map);
      measureShown = true;
    };
    // Terra Draw fires ``change`` on every click AND cursor move during drawing,
    // so the readout updates live as the shape is drawn (first click → first move).
    draw.on("change", refreshDraftMeasure);

    // Persist a completed shape (any kind): build the request from the finished
    // feature + the current draw style, drop the transient draw feature (the
    // persisted copy paints via the Meridian render layer once the list
    // refetches), POST it, then deselect the tool.
    draw.on("finish", (id) => {
      const current = drawRef.current;
      if (current === null) {
        return;
      }
      const finished = current.getSnapshotFeature(id);
      if (finished === undefined) {
        return;
      }
      const request = finishedFeatureToRequest(
        finished,
        useAnnotationToolStore.getState().drawStyle,
      );
      if (request === null) {
        return; // Not a persistable draw (e.g. the render / select mode).
      }
      current.removeFeatures([id]);
      // The arrowhead's size is no longer baked at draw time — it's a fixed pixel
      // size recomputed from the current zoom by the render layer (operator request
      // 2026-06-12; annotation-map-features.annotationsToArrowFeatures).
      createMutateRef.current(request);
      // Deselect the tool once the shape is committed (D14 amendment).
      useAnnotationToolStore.getState().clearActiveTool();
      hideDraftMeasure(); // the persisted label takes over from the live readout
    });

    // Annotation selection: with NO draw tool active (the default cursor), a map
    // click selects the annotation under the cursor (by ``annotation_id`` read
    // off a Meridian render-layer feature), defers to an entity glyph that owns
    // the click, or clears the selection on empty space. Only acts when no tool
    // is active, so it never interferes with drawing.
    //
    // The precedence lives in :func:`resolveAnnotationClick` rather than here so
    // it is pinned by tests: this handler is map-wide while every glyph handler
    // is layer-scoped, so both run on the same click and the order is mount-order
    // luck (the hazard ``background-deselect`` documents on the deselect path).
    const onSelectClick = (event: MapMouseEvent): void => {
      const store = useAnnotationToolStore.getState();
      if (store.activeTool !== null) {
        return;
      }
      const hit = map
        .queryRenderedFeatures(event.point)
        .find((feature) => typeof feature.properties?.annotation_id === "string");
      const annotationId = hit?.properties?.annotation_id;
      const action = resolveAnnotationClick({
        // An entity glyph under the cursor owns this click (REQ-UI-105) — its
        // handler writes the selection bus, which clears the annotation as it
        // sets the entity. Shared hit-test with the deselect path.
        hitsEntityLayer: !isBackgroundClick(map, event.point),
        annotationIdUnderCursor: typeof annotationId === "string" ? annotationId : null,
      });
      switch (action.type) {
        case "defer":
          return;
        case "select":
          store.setSelectedId(action.annotationId);
          return;
        case "clear":
          store.setSelectedId(null);
          return;
      }
    };
    map.on("click", onSelectClick);

    drawRef.current = draw;

    return () => {
      if (isMapUsable(map)) {
        map.off("click", onSelectClick);
        try {
          draw.stop();
        } catch {
          // Map already removed; the layers went with it.
        }
      }
      measurePopup.remove();
      drawRef.current = null;
    };
  }, [map]);

  // Switch the active Terra Draw mode when the palette tool changes. A tool
  // auto-deselects after completing a shape (see the finish handler), so this
  // also returns to the idle ``render`` mode then.
  useEffect(() => {
    const draw = drawRef.current;
    if (draw === null) {
      return;
    }
    hideDraftMeasureRef.current(); // drop a stale draw readout when the tool changes
    draw.setMode(toolToDrawMode(activeTool));
  }, [activeTool]);

  // Crosshair cursor while a DRAW tool is active (feature-spec §8.1). The "edit"
  // (move) tool is excluded — use-annotation-drag owns its grab/grabbing cursor.
  useEffect(() => {
    if (map === null || !isMapUsable(map)) {
      return undefined;
    }
    const canvas = map.getCanvas();
    const drawing = activeTool !== null && activeTool !== "edit";
    canvas.style.cursor = drawing ? "crosshair" : "";
    return () => {
      canvas.style.cursor = "";
    };
  }, [map, activeTool]);
}
